import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges
} from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { FormModelControls } from '@shared/form-elements/model/form-model-controls';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import isEqual from 'lodash.isequal';
import { distinctUntilChanged } from 'rxjs/operators';
import {
    CardMessageData,
    FormModel,
    ParsedCardMessage
} from '@features/basket-shared/basket-gift-card-message-form/basket-gift-card-message-form.model';
import { TranslateSafePipe } from '@shared/translate-safe/translate-safe.pipe';
import { InputTextareaComponent } from '@shared/form-elements/input/input-textarea/input-textarea.component';
import { InputComponent } from '@shared/form-elements/input/input/input.component';

@UntilDestroy()
@Component({
    selector: 'app-basket-gift-card-message-form',
    templateUrl: './basket-gift-card-message-form.component.html',
    styleUrls: ['./basket-gift-card-message-form.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [FormsModule, ReactiveFormsModule, InputComponent, InputTextareaComponent, TranslateSafePipe]
})
export class BasketGiftCardMessageFormComponent implements OnChanges, OnInit {
    private readonly maxNumOfCharsPerLine: number = 34;
    public readonly maxNumOfChars: number = 160;
    public form: UntypedFormGroup;

    @Input() public data?: string;
    @Input() public allowMessage: boolean = false;

    @Output() public messageChange: EventEmitter<CardMessageData> = new EventEmitter<CardMessageData>();

    constructor(private formBuilder: UntypedFormBuilder) {}

    public ngOnInit(): void {
        const { from = '', to = '', lines = [''] } = this.data ? JSON.parse(this.data) : {};

        const lineString = Array.isArray(lines) ? lines.join(' ') : lines;
        const formControls: FormModelControls<FormModel> = {
            from: [from],
            to: [to],
            lines: [lineString, Validators.maxLength(200)]
        };

        this.form = this.formBuilder.group(formControls);
        this.form.valueChanges
            .pipe(
                distinctUntilChanged((prev, curr) => isEqual(prev, curr)),
                untilDestroyed(this)
            )
            .subscribe(data => {
                const giftCardText: ParsedCardMessage = {
                    from: data.from,
                    to: data.to,
                    lines: this.splitTextIntoLines(data.lines)
                };
                if (this.allowMessage) {
                    this.messageChange.emit({ giftCardText: JSON.stringify(giftCardText) });
                }
            });

        this.setFormEnabled(this.allowMessage);
    }

    public ngOnChanges(changes: SimpleChanges): void {
        const { currentValue } = changes.allowMessage || {};
        this.setFormEnabled(currentValue || false);
    }

    private setFormEnabled(enabled: boolean): void {
        if (enabled) {
            this.form?.enable();
            return;
        }

        this.form?.disable();
    }

    // we need to split the text into lines, to make sure prime cargo receive in the correct format
    // since they will print each line directly into the card
    private splitTextIntoLines(message: string): string[] {
        const messageByWords = this.checkWordSizes(message);
        let lineIndex: number = 0;
        let previousLine = lineIndex;
        let totalPerLine = 0;
        return messageByWords
            .reduce((acc: string[], word: string) => {
                // measure each word size, and make sure it will fit in the line
                totalPerLine += word.length + 1; // the + one is because of the space we need to join the words;
                if (totalPerLine > this.maxNumOfCharsPerLine) {
                    totalPerLine = 0;
                    lineIndex++;
                }

                // make sure we cont the previous word in the next line
                // since we change the line we are adding words
                if (previousLine !== lineIndex) {
                    previousLine = lineIndex;
                    totalPerLine = word.length;
                }

                let line: string = acc[lineIndex] || '';
                line += `${word} `;
                acc[lineIndex] = line;
                return acc;
            }, [])
            .map(line => line.trim());
    }

    // only to make sure the users doesn't send a big word without spaces, we will force the split
    // I don't think there is words bigger than 40 chars, for instance
    private checkWordSizes(message: string): string[] {
        return message
            .split(' ')
            .flatMap(word => {
                const smallWords = [];
                if (word.length < this.maxNumOfCharsPerLine) {
                    smallWords.push(word);
                } else {
                    const howManyWords = word.length / this.maxNumOfCharsPerLine;

                    for (let i = 0; i < howManyWords; i++) {
                        const partOfWord = word.substr(i * this.maxNumOfCharsPerLine, this.maxNumOfCharsPerLine);
                        smallWords.push(partOfWord);
                    }
                }

                return smallWords;
            })
            .filter(words => words.length);
    }
}
